import React, { ForwardedRef, forwardRef, useImperativeHandle, useState } from "react"
import Utils from "../../libs/Utils"
import GUI, { IGUIProps, TGUIPropper } from "../GUI"
import { UpdateEvent } from "../GUIEvents"
import IconButton from "../Button/IconButton"
import ChevronLeft from "../Icons/ChevronLeft"
import ChevronRight from "../Icons/ChevronRight"
import Dropdown from "../Dropdown/Dropdown"
import _ from "lodash"

function WeekHeader(){
    const cells = Utils.WEEK_SHORT.map( (val,i) => <div key={i}>{val}</div>)
    return <div className="week-header medium-text">{cells}</div>
}

type TDayProps = {
    date:Date
    selected:boolean|null
    otherMonth:boolean
    cellProps?:TGUIPropper<"div">
    cellRenderer?:(date:Date) => JSX.Element
    onDate:(e:React.MouseEvent<HTMLDivElement>, date:Date) => void
}

function Day(props:TDayProps) {
    const cellProps = GUI.getProps(props.cellProps, props.date)
    const {className,onClick:_onClick,...cprops} = cellProps
    const onClick = (e:React.MouseEvent<HTMLDivElement>) => {
        _onClick?.(e)
        !props.selected && props.onDate(e, props.date)
    }
    const cell = props.cellRenderer ? props.cellRenderer(props.date) : props.date.getDate()
    const isToday = Utils.compDate(props.date, new Date()) == 0
    return <div {...cprops} className={GUI.joinClass("date", className, props.selected ? "selected" : "", props.otherMonth ? "grey" : "", isToday ? "today" : "")} onClick={onClick}>{cell}</div>
}

type TWeekProps = {
    day0:Date,
    month:number
    selected:Date|null
    visible:Date
    cellProps?:TGUIPropper<"div">
    cellRenderer?:(date:Date) => JSX.Element
    onDate:(e:React.MouseEvent<HTMLDivElement>, date:Date) => void
}
function Week(props:TWeekProps) {
    const cells : JSX.Element[] = []
    var date = props.day0
    const month = new Date(props.visible).getMonth()
    for(var i=0; i<7; i++) {
        const selected = !!props.selected && Utils.compDate(props.selected, date)==0
        cells.push(<Day date={new Date(date)} selected={selected} key={date.toString()} otherMonth={date.getMonth()!=month} onDate={props.onDate} cellRenderer={props.cellRenderer} cellProps={props.cellProps}/>)
        date = new Date( date.setDate(date.getDate()+1) )
    }
    return <div className="week-row">{cells}</div>
}


export type TCalendarProps = IGUIProps & {
    value?:Date
    defaultValue?:Date
    visible?:Date
    minYear?:number
    maxYear?:number
    cellProps?:TGUIPropper<"div">
    readOnly?:boolean
    cellRenderer?:(date:Date) => JSX.Element
    onUpdate?:(e:UpdateEvent<Date>) => void
}

export interface ICalendar {
    value:Date|null
}

const MonthList = Utils.MONTHS.map((month,i) => ({id:i,text:month}))

export default forwardRef((props:TCalendarProps, ref?:ForwardedRef<ICalendar>) => {
    const isManaged = props.hasOwnProperty("value")
    const [selected,setSelected] = useState<Date|null>(props.value ?? props.defaultValue ?? null)
    const current = isManaged ? props.value! : selected
    const [visible,setVisible] = useState<Date>(new Date(props.visible ?? current ?? new Date()))
    const YearList = _.range(props.minYear || 1970, props.maxYear || new Date().getFullYear()+1).reverse()

    const year = visible.getFullYear(), month = visible.getMonth()
    const d1 = new Date(year, month, 1), d0 = d1.getDay()==0 ? d1 : new Date( new Date(d1).setDate(1-d1.getDay()) )
    const rows:JSX.Element[] = []

    useImperativeHandle(ref, () => ({
        get value(){return current}
    }), [current])

    const onDate = (e:React.MouseEvent<HTMLDivElement>, date:Date) => {
        if (props.readOnly) return
        if (!isManaged) setSelected(date)
        props.onUpdate?.(new UpdateEvent(e.nativeEvent, date, null))
    }

    var date = d0
    for(var i=0; i<6; i++) {
        rows.push(<Week key={i} day0={new Date(date)} month={month} selected={current} visible={visible} onDate={onDate} cellRenderer={props.cellRenderer} cellProps={props.cellProps}/>)
        date = new Date(date.setDate(date.getDate()+7))
    }
    const onNav = (amount:number) => (e:React.MouseEvent<HTMLButtonElement>) => {
        var next = new Date(visible)
        setVisible(new Date(next.setMonth(next.getMonth()+amount)))
    }
    const onYear = (e:UpdateEvent<number>) => setVisible(new Date(visible.setFullYear(e.value!)))
    const onMonth = (e:UpdateEvent<number>) => setVisible(new Date(visible.setMonth(e.value!)))
    return <div className={GUI.joinClass("calendar", props.className)}>
        <div className="header flex middle">
            <IconButton onClick={onNav(-1)}><ChevronLeft/></IconButton>
            <div className="flex1 align-center flex center middle gap-8">
                <Dropdown className="year" options={YearList} value={year} onUpdate={onYear} hideArrow={true} anchorProps={{width:"match"}}/> - 
                <Dropdown className="month" options={MonthList} value={month} onUpdate={onMonth} hideArrow={true} anchorProps={{width:"match"}}/>
            </div>
            <IconButton onClick={onNav(1)}><ChevronRight/></IconButton>
        </div>
        <WeekHeader/>
        <div className="body">{rows}</div>
    </div>
})